import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Flex,
  VStack,
  HStack,
  Text,
  IconButton,
  Button,
  Progress,
  Avatar,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useToast,
  Portal,
  useDisclosure,
} from "@chakra-ui/react";

import { FiBell, FiUser, FiX, FiTrash2 } from "react-icons/fi";

import UploadIcon from "../../assets/upload.svg";
import FileIcon from "../../assets/Rectangle 1.svg";

import Sidebar from "components/sidebar/Sidebar";
import { SidebarContext } from "contexts/SidebarContext";
import Navbar from "components/navbar/NavbarAdmin.js";
import {
  getActiveRoute,
  getActiveNavbar,
  getActiveNavbarText,
} from "helper/UploadFilePageHelper";
import routes from "routes.js";

export default function FileUploadUI() {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const toast = useToast();
  const { onOpen } = useDisclosure();

  const handleFileChange = (event) => {
    const newFiles = Array.from(event.target.files).map((file) => ({
      name: file.name,
      size: file.size,
      progress: 100,
      status: "uploading",
    }));
    setFiles((prev) => [...prev, ...newFiles]);
  };

  const handleRemove = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    toast({
      title: "File removed.",
      status: "info",
      duration: 2000,
      isClosable: true,
    });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    const newFiles = droppedFiles.map((file) => ({
      name: file.name,
      size: file.size,
      progress: 100,
      status: "uploading",
    }));
    setFiles((prev) => [...prev, ...newFiles]);
  };

  const handleNextClick = () => {
    // Create dummy data similar to what "Select folder" shows
    const demoDocumentType = {
      id: 5,
      name: "",
      description: "",
      location: "",
      isActive: true,
    };

    // Create dummy files that would normally be populated by selecting a folder
    const dummyFiles = [
      {
        file_name: "SARAH WILLIAMS.pdf",
        file_type: "application/pdf",
        file_path: "/assets/pdfs/contracts/SARAH WILLIAMS CONTRACT.pdf",
      },
      {
        file_name: "SOPHIA HARRIS – PHSP Agreement.pdf",
        file_type: "application/pdf",
        file_path: "/assets/pdfs/contracts/SOPHIA HARRIS - PHSP Agreement.pdf",
      },
    ];

    // Navigate to module-files-upload-page with dummy data and pre-populated files
    navigate("/module-files-upload-page", {
      state: {
        selectedDocumentTypeID: 5,
        selectedDocumentType: [demoDocumentType],
        prePopulatedFiles: dummyFiles, // Pass the dummy files to be automatically populated
      },
    });
  };

  return (
    <SidebarContext.Provider value={{}}>
      <Sidebar />

      <Box
        float="right"
        minHeight="100vh"
        height="100vh"
        overflow="hidden"
        position="relative"
        w={{ base: "100%", xl: "calc(100% - 290px)" }}
        maxWidth={{ base: "100%", xl: "calc(100% - 290px)" }}
        transition="all 0.33s cubic-bezier(0.685, 0.0473, 0.346, 1)"
        transitionDuration=".2s, .2s, .35s"
        transitionProperty="top, bottom, width"
        transitionTimingFunction="linear, linear, ease"
        display="flex"
        flexDirection="column"
      >
        <Portal>
          <Box>
            <Navbar
              onOpen={onOpen}
              logoText={"Horizon UI Dashboard PRO"}
              brandText={getActiveRoute(routes)}
              secondary={getActiveNavbar(routes)}
              message={getActiveNavbarText(routes)}
              fixed={false}
              navbarHeading="File Upload"
            />
          </Box>
        </Portal>

        {/* Card Section */}
        <Flex flex="1" bg="gray.50" p={10} overflow="hidden" pt="100px">
          <Box
            bg="white"
            borderRadius="lg"
            boxShadow="md"
            border="1px solid"
            borderColor="gray.200"
            w="100%"
            h="100%"
            p={6}
            display="flex"
            flexDirection="column"
          >
            {/* Upload Area */}
            <Flex
              flexShrink={0}
              border="2px dashed"
              borderColor={isDragging ? "blue.500" : "gray.300"}
              borderRadius="lg"
              bg={isDragging ? "blue.50" : "gray.50"}
              justify="center"
              align="center"
              direction="column"
              textAlign="center"
              p={10}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <Box
                as="img"
                src={UploadIcon}
                alt="Upload"
                boxSize="80px"
                mb={4}
              />
              <Text fontSize="lg" fontWeight="medium" mt={3}>
                Drag your file(s) here to upload
              </Text>
              <Text color="gray.500" mb={3}>
                OR
              </Text>
              <Button
                as="label"
                variant="outline"
                color="gray.700"
                bg="white"
                cursor="pointer"
              >
                Browse Files
                <input
                  type="file"
                  multiple
                  hidden
                  onChange={handleFileChange}
                />
              </Button>
            </Flex>

            <Text fontSize="sm" color="gray.500" mt={4}>
              Only support .PDF, .DOCX and ZIP files
            </Text>

            {/* Scrollable file list */}
            <Box flex="1" mt={4} overflowY="auto" pr={2}>
              <VStack spacing={4} align="stretch">
                {files.map((file, index) => (
                  <Flex
                    key={index}
                    align="center"
                    bg="white"
                    p={4}
                    borderRadius="lg"
                    border="1px solid"
                    borderColor="gray.200"
                    boxShadow="sm"
                  >
                    <Box
                      as="img"
                      src={FileIcon}
                      alt="file"
                      boxSize="28px"
                      mr={4}
                    />
                    <Box flex="1">
                      <Text fontWeight="medium">{file.name}</Text>
                      <Text fontSize="sm" color="gray.500">
                        {(file.size / (1024 * 1024)).toFixed(1)} MB
                      </Text>
                      {file.status === "uploading" && (
                        <Progress
                          mt={2}
                          value={file.progress}
                          size="sm"
                          colorScheme="blue"
                          borderRadius="full"
                          w="100%"
                        />
                      )}
                      {file.status === "error" && (
                        <Text mt={2} fontSize="sm" color="red.500">
                          Upload Failed
                        </Text>
                      )}
                    </Box>
                    <IconButton
                      aria-label="Remove file"
                      icon={file.status === "error" ? <FiTrash2 /> : <FiX />}
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemove(index)}
                      ml={2}
                    />
                  </Flex>
                ))}
              </VStack>
            </Box>

            {/* Buttons fixed at bottom inside card */}
            <Flex
              mt={4}
              pt={4}
              justify="flex-end"
              position="sticky"
              bottom="0"
              bg="white"
              zIndex="10"
              // borderTop="1px solid"
              borderColor="gray.200"
            >
              <Button
                variant="outline"
                borderColor="gray.300"
                color="gray.700"
                bg="white"
                borderRadius="md"
                _hover={{ bg: "gray.100" }}
                mr={3}
              >
                Cancel
              </Button>
              <Button
                onClick={handleNextClick}
                colorScheme="blue"
                bg="blue.500"
                color="white"
                borderRadius="md"
                _hover={{ bg: "blue.600" }}
                disabled={files.length === 0}
                opacity={files.length === 0 ? 0.6 : 1}
                cursor={files.length === 0 ? "not-allowed" : "pointer"}
              >
                Next
              </Button>
            </Flex>
          </Box>
        </Flex>
      </Box>
    </SidebarContext.Provider>
  );
}
